import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, notification, Row } from "ant-design-vue";
import lodash from "lodash";
import { toRaw } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../../../app.logic";
import { setAppPageHeader } from "../../../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../../../i18n";
import router from "../../../../../router";
import store from "../../../../../store";
import { createButtonSave } from "../../../../../utils/button";
import { initUseForm, watchRulesChildren } from "../../../../../utils/form";
import { createLoading } from "../../../../../utils/loading";
import { required, requiredWhen, validateError } from "../../../../../utils/validate";
import { createConditionAction, createConditionForm } from "./tab2-data.condition";
import { createField } from "./tab2-data.section";

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/userBank";

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });

export const onClickSave = ({ props, data, formParams }) => {
    const { routerPrefix } = props;
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callSave({ form: { ...data.useForm.modelRef, ...formParams } })
                .finally(() => setIsLoadingApp(false))
                .then(() => {
                    notification["success"]({ message: i18n.global.t("views.edit.success") });
                    router.push({ name: routerPrefix, query: { _timestamp: new Date().getTime() } });
                });
        })
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ["**"],
            page: 0,
            size: 1,
            where: [
                { name: "refUser.id", type: "UUID", operator: "=", value: id },
                { name: "isTemp", type: "Boolean", operator: "=", value: false }
            ],
            children: [{ name: "attachments", select: ["*"], page: 0, size: 1000000, sort: [{ name: "createDate", type: "ASC" }] }]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createForm = () => {
    return {
        refUser: null,
        refBank: null,
        branch: null,
        branchCode: null,
        refType: null,
        bankCust: null,
        bankAccount: null,
        bankName: null,
        refStatus: null,
        isTemp: null,
        attachments: [],
        statuses: []
    };
};

export const createRules = ({ key }) => {
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refBank`]: [required],
        [`${prefix}branch`]: [required],
        [`${prefix}branchCode`]: [required],
        [`${prefix}refType`]: [required],
        [`${prefix}bankCust`]: [required],
        [`${prefix}bankAccount`]: [required],
        [`${prefix}bankName`]: [required]
    };
};

export const createRulesAttachment = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {};
    model?.attachments?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}attachments.${index}.documentName`] = [required];
            rules[`${prefix}attachments.${index}.size`] = [requiredWhen({ when: () => model.attachments[index].attribute?.isRequiredAttachment })];
        }
    });
    return rules;
};

export const loadForm = ({ props, data, refs, companyId }) => {
    setIsLoading(true);
    const { form, routerPrefix } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ props, data, refs, companyId, id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then(page => (page.data.length === 0 ? { data: [useForm.modelRef] } : page))
        .then(page => initUseForm({ data, page, routerPrefix }))
        .then(() => loadDefaultForm({ props, data, refs, companyId }))
        .then(() => initUseForm({ data, page: { data: [modelRef] }, routerPrefix }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = () => {
    const promises = [];
    return Promise.all(promises);
};

export const initForm = ({ props, data, refs, companyId }) => {
    data.form = createForm({ props, data, refs, companyId });
    data.rules = createRules({ props, data, refs, companyId });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, companyId, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, companyId, isLoading });
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.attachments.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesAttachment({ props, data, refs }),
        itemsKey: "attachments"
    });
    loadForm({ props, data, refs, companyId }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    default: () => createAppPageHeaderDefault({ props, data, refs, companyId })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs }) => {
    const col1 = [];
    const col2 = [];
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive } = modelRef;
    const conditionAction = createConditionAction();
    const canEdit = conditionAction.canEdit({ props, record: initialModel });
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(
            createButtonSave({
                onClick: () => {
                    onClickSave({ props, data, refs });
                },
                classStyle: "m-1"
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};
